import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LogoPagaliImg from '../../assets/logo-pagali-color@2x.png';

// Icons as simple SVG components
const PersonIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const BusinessIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const OngIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const DeveloperIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
  </svg>
);

const RestaurantIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  </svg>
);

const DeliveryIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 100 4 2 2 0 000-4zm10 0a2 2 0 100 4 2 2 0 000-4z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M1 3h15l3 9H3z" />
  </svg>
);

const EventIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
  </svg>
);

const TourismIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-5 h-5 text-pagali-primary" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

type AccountType = 'PESSOA' | 'EMPRESA' | 'ONG' | 'DEVELOPER' | 'ENTREGADOR' | 'RESTAURANTE' | 'PROMOTOR' | 'TURISMO';

interface AccountOption {
  type: AccountType;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const accountOptions: AccountOption[] = [
  {
    type: 'PESSOA',
    title: 'Conta Pessoa',
    description: 'Para pagar contas, enviar dinheiro e compras do dia-a-dia.',
    icon: <PersonIcon />
  },
  {
    type: 'EMPRESA',
    title: 'Conta Empresa',
    description: 'Empresas, lojas e-commerce, comercios e prestadores de servicos.',
    icon: <BusinessIcon />
  },
  {
    type: 'ONG',
    title: 'ONG / Associacao',
    description: 'Para campanhas de doacao, gestao de projetos e transparencia.',
    icon: <OngIcon />
  },
  {
    type: 'DEVELOPER',
    title: 'Conta Developer',
    description: 'Acesso API, sandbox e ferramentas de integracao.',
    icon: <DeveloperIcon />
  },
  {
    type: 'RESTAURANTE',
    title: 'Restaurante',
    description: 'Regista o teu restaurante e recebe pedidos online.',
    icon: <RestaurantIcon />
  },
  {
    type: 'PROMOTOR',
    title: 'Promotor de Eventos',
    description: 'Organiza eventos, vende bilhetes e gere inscrições.',
    icon: <EventIcon />
  },
  {
    type: 'ENTREGADOR',
    title: 'Entregador',
    description: 'Faz entregas com o Pagali e ganha dinheiro a teu ritmo.',
    icon: <DeliveryIcon />
  },
  {
    type: 'TURISMO',
    title: 'Promotor Turístico',
    description: 'Regista experiências e tours com o iWalkCV.',
    icon: <TourismIcon />
  }
];

const isSandbox = process.env.REACT_APP_ENV !== 'production';

const AccountTypeSelection: React.FC = () => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState<AccountType>('PESSOA');

  // Conta Developer só disponível em sandbox
  const visibleOptions = accountOptions.filter(
    o => o.type !== 'DEVELOPER' || isSandbox
  );

  const handleContinue = () => {
    if (selectedType === 'ENTREGADOR') {
      navigate('/driver/register');
    } else {
      navigate('/register/form', { state: { accountType: selectedType } });
    }
  };


  return (
    <div className="min-h-screen bg-pagali-primary flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-8">
        {/* Close button */}
        <div className="flex justify-end mb-4">
          <Link
            to="/login"
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Link>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <img
            src={LogoPagaliImg}
            alt="Pagali"
            className="h-10 mx-auto mb-6"
          />
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">
            Como quer usar o Pagali?
          </h1>
          <p className="text-gray-500">
            Escolha o tipo de conta ideal para si.
          </p>
        </div>

        {/* Account Type Options */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          {visibleOptions.map((option) => (
            <button
              key={option.type}
              onClick={() => setSelectedType(option.type)}
              className={`relative flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all ${
                selectedType === option.type
                  ? 'border-pagali-primary bg-pagali-primary/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {/* Check badge */}
              {selectedType === option.type && (
                <div className="absolute top-1.5 right-1.5">
                  <CheckIcon />
                </div>
              )}

              {/* Icon */}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                selectedType === option.type
                  ? 'bg-pagali-primary text-white'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {option.icon}
              </div>

              {/* Content */}
              <h3 className="font-medium text-gray-800 text-xs text-center leading-tight">{option.title}</h3>
              <p className="text-xs text-gray-500 text-center mt-1 leading-snug hidden sm:block">{option.description}</p>
            </button>
          ))}
        </div>

        {/* Continue Button */}
        <button
          onClick={handleContinue}
          className="w-full bg-pagali-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-pagali-primary/90 transition-colors flex items-center justify-center gap-2"
        >
          Continuar
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </button>

        {/* Login Link */}
        <p className="text-center mt-6 text-gray-600">
          Ja tem conta?{' '}
          <Link to="/login" className="text-pagali-primary font-medium hover:underline">
            Entrar
          </Link>
        </p>
      </div>
    </div>
  );
};

export default AccountTypeSelection;
